import React from "react";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { Box, styled } from "@mui/material";

// Import all icon images
import html5 from "../static/images/icons/HTML5.png";
import css3 from "../static/images/icons/CSS3.png";
import js from "../static/images/icons/javascript.png";
import bootstrap from "../static/images/icons/bootstrap.png";
import npm from "../static/images/icons/npm.png";
import reactIcon from "../static/images/icons/react.png";
import reduxIcon from "../static/images/icons/redux.png";
import laravel from "../static/images/icons/laravel.png";
import api from "../static/images/icons/api.png";
import json from "../static/images/icons/json.png";
import postman from "../static/images/icons/postman.png";
import github from "../static/images/icons/github.png";
import frontEnd from "../static/images/frontEnd.png";
import backtEnd from "../static/images/backendpng.png";

// --- Styled Components ---
const StyledSection = styled(Box)(({ theme }) => ({
  /* backgroundColor: '#121212', */
  color: theme.palette.grey[50],
  padding: theme.spacing(12, 0),
  position: 'relative',
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  fontWeight: 700,
  color: '#1976d2',
  textAlign: 'center',
  fontSize: '3.5rem',
  textShadow: '0 0 10px rgba(97, 218, 251, 0.4)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.5rem',
  },
}));

const LeadText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(8),
  fontSize: '1.25rem',
  lineHeight: 1.6,
  maxWidth: '900px',
  margin: '0 auto',
  textAlign: 'center',
  /* color: theme.palette.grey[300],  */
  color: '#525f7f',
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(4),
  color: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.8rem',
  },
}));

const AnimatedBulletItem = styled(motion.p)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  fontSize: '1.1rem',
  lineHeight: 1.5,
  /* color: theme.palette.grey[100], */
  color: '#525f7f',
  // Use a different color for the svg
  '& svg': {
    flexShrink: 0,
    height: '1.2em',
    width: '1.2em',
    verticalAlign: 'middle',
    marginRight: theme.spacing(1),
    fill: theme.palette.secondary.main,
    transition: 'fill 0.3s ease-in-out, transform 0.3s ease-in-out',
  },
  '&:hover svg': {
    fill: '#FFC107', // Gold accent on hover
    transform: 'scale(1.1) rotate(5deg)',
  },
}));

const IconWrapper = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  margin: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.2) translateY(-8px)',
  },
  '&::before': {
    content: 'attr(data-name)',
    position: 'absolute',
    bottom: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'nowrap',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.3s, visibility 0.3s',
    fontSize: '0.875rem',
    zIndex: 10,
    backdropFilter: 'blur(5px)',
  },
  '&:hover::before': {
    opacity: 1,
    visibility: 'visible',
  },
}));

const TechIcon = styled('img')({
  width: '30px',
  height: '30px',
  objectFit: 'contain',
  filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.4))',
  backgroundColor: '#FFF',
  borderRadius: '50%',
  padding: '8px',
});

// --- Main Component ---
export default function About() {
  const { ref: aboutSectionRef, inView: aboutSectionInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { ref: frontendGridRef, inView: frontendGridInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const { ref: backendGridRef, inView: backendGridInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const listVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div  className="about-Section">
    <StyledSection ref={aboutSectionRef}>
      <Container maxWidth="lg">
        <Title component="h1" initial="hidden" animate={aboutSectionInView ? "visible" : "hidden"} variants={itemVariants} sx={{ fontSize: '2.5rem' }} >
          ✨ My Expertise
        </Title>
        <LeadText component="p" initial="hidden" animate={aboutSectionInView ? "visible" : "hidden"} variants={itemVariants} sx={{ fontSize: '1.05rem' }}>
          An enthusiastic Full Stack Developer, I'm fueled by a relentless curiosity for diverse tech stacks. I focus on delivering high-quality web applications from concept to deployment. I thrive on bringing ideas to life
        </LeadText>

        {/* Front End Development Section */}
        <Grid container spacing={8} sx={{ my: 8 }} alignItems="center" flexDirection={{ xs: 'column-reverse', md: 'row' }}>
          <Grid size={6} ref={frontendGridRef}>
            <motion.div initial="hidden" animate={frontendGridInView ? "visible" : "hidden"} variants={listVariants}>
              <CardTitle component="h3" variants={itemVariants} sx={{ fontSize: '1.9rem' }} >
                💻 Front End Development
              </CardTitle>
              <Box sx={{ mb: 4 }}>
                {[
                  { name: "HTML5", src: html5 },
                  { name: "CSS3", src: css3 },
                  { name: "JavaScript", src: js },
                  { name: "Bootstrap", src: bootstrap },
                  { name: "NPM", src: npm },
                  { name: "React", src: reactIcon },
                  { name: "Redux", src: reduxIcon },
                ].map((icon, index) => (
                  <IconWrapper key={index} data-name={icon.name} variants={itemVariants}>
                    <TechIcon src={icon.src} alt={icon.name} />
                  </IconWrapper>
                ))}
              </Box>
              <Box>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
                  Creating highly **responsive and interactive SPAs** using React.js.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" /><line x1="12" y1="20" x2="12" y2="10" /></svg>
                  Building dynamic and intuitive user interfaces and dashboards.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
                  Managing state effectively with **Redux** for complex applications.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                  Ensuring cross-browser compatibility and optimized performance.
                </AnimatedBulletItem>
              </Box>
            </motion.div>
          </Grid>
          <Grid size={6}>

            <img width="100%" src={frontEnd} alt="frontEnd" />
          </Grid>
        </Grid>

        {/* Backend Development Section */}
        <Grid container spacing={8} sx={{ my: 8 }} alignItems="center">
          <Grid size={6}>
            {  /* <BackendSVG inView={backendGridInView} /> */}
            <img width="100%" src={backtEnd} alt="backtEnd" />
          </Grid>
          <Grid size={6} ref={backendGridRef}>
            <motion.div initial="hidden" animate={backendGridInView ? "visible" : "hidden"} variants={listVariants}>
              <CardTitle component="h3" variants={itemVariants} sx={{ fontSize: '1.9rem' }}>
                ⚙️  Backend Development
              </CardTitle>
              <Box sx={{ mb: 4 }}>
                {[
                  { name: "Laravel", src: laravel },
                  { name: "API", src: api },
                  { name: "JSON", src: json },
                  { name: "Postman", src: postman },
                  { name: "GitHub", src: github },
                ].map((icon, index) => (
                  <IconWrapper key={index} data-name={icon.name} variants={itemVariants}>
                    <TechIcon src={icon.src} alt={icon.name} />
                  </IconWrapper>
                ))}
              </Box>
              <Box>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14.88V16a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-1.12" /><path d="M4 14.88h16" /><path d="M22 14.88v-1.12a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v1.12" /><path d="M20 14.88V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.12" /><path d="M12 2v12.88" /><path d="M12 2v12.88L8 10" /><path d="M12 2v12.88L16 10" /></svg>
                  My back-end capabilities ensure the creation of secure and **efficient server-side systems**.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" ry="2" /><path d="M12 11V6a2 2 0 0 1 2-2h2v4" /><path d="M12 11V6a2 2 0 0 1-2-2h-2v4" /></svg>
                  Building API endpoints and ensuring high levels of safety and quality.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M4 11h16" /><path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-4 4" /><path d="M12 2a4 4 0 0 0-4 4v1a4 4 0 0 0 4 4" /></svg>
                  Hosting and maintaining websites on **virtual machine instances** along with integration of databases.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                  Experience in working with cPanel and server environments.
                </AnimatedBulletItem>
                <AnimatedBulletItem variants={itemVariants} sx={{ fontSize: '1rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" /><path d="M16 8v8h-8V8h8z" /><line x1="12" y1="12" x2="12" y2="12" /></svg>
                  Proficiency in server management and testing with tools like **Postman**.
                </AnimatedBulletItem>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </StyledSection>
    </div>
  );
}